// Node Modules
import { RichText } from 'prismic-reactjs';
import { nanoid } from 'nanoid';

// Components
import Button from '@/components/global-components/Button';
import MobilePhotoContainer from '@/components/global-components/photos/MobilePhotoContainer'
import Photo from '@/components/global-components/photos/Photo';
import Modal, { useModal }  from '@/components/global-components/modal/Modal';
import PhotoCarousel from '@/components/global-components/photos/PhotoCarousel';

// Themes
import theme from '@/themes/index';

export const StyledCard = ({
  children,
  isDesktop,
  removeImage = false,
  containerStyle = {},
  infoContainerStyle = {},
  infoButtonStyle = {},
  content: {
    cardContent = {},
    image = {},
    imageUrl,
    secondaryImages,
  },
  imageStyles,
}) => {
  const {
    isShowing,
    toggle, 
  } = useModal();

  const imagesMapped = secondaryImages?.map(item => {
    return {
      image: item.secondaryImage,
    }
  })

  const getCustomInfoContainerStyles = () => {
    if (cardContent?.contentAlignment === 'Center') {
      return {
        justifyContent: 'center',
      };
    }
  };

  const getDefaultInfoCard = (buttonStyle = {}) => {
    return (
      <>
        <div className='default-card-content'>
          <h3 className='title'>{cardContent.title}</h3>
          <h3 className='subtitle'>{cardContent.subTitle}</h3>
          <RichText render={cardContent.description} />
          <div className='default-card-button-container'>
            {cardContent.buttons?.map(({
              button, 
            }) => (
              <Button
                key={nanoid()}
                text={button.title}
                href={button.url}
                target={button.openInAnotherPage ? '_blank' : '_self'}
                styles={{
                  ...buttonStyle,
                  width: isDesktop ? 'fit-content' : '100%',
                  padding: '0 1rem',
                }} />
            ))}
          </div>
        </div>
        <style jsx>{`
          div.default-card-content h3 {
            margin-block-start: 1rem;
            margin-block-end: 0;
            margin-inline-start: 0;
            margin-inline-end: 0;
          }

          div.default-card-content h3.title {
            font-family: ${theme.fontFamilies.montserratSemiBold};
          }

          div.default-card-content h3.subtitle {
            font-family: ${theme.fontFamilies.montserratLight};
          }

          @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
            div.default-card-content {
              padding: 3.25rem 0 0 0;
              width: 100%;
            }

            div.default-card-content h3 {
              line-height: 4.1875rem;
              margin-block-start: 0;
            }
          }
        `}</style>
        <style global jsx>{`
          div.default-card-button-container a {
            margin: 0 auto;
          }

          div.default-card-content p {
            font-family: ${theme.fontFamilies.montserrat};
            line-height: 1.225rem;
          }

          @media only screen and (min-width: ${theme.breakpoints.tabletMin}) {
            div.default-card-content p {
              font-size: 1.5rem;
              line-height: 2.25rem;
            }
          }

          @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
            div.default-card-button-container a {
              margin: 0;
            }
          }
        `}</style>
      </>
    );
  };

  const getSecondaryImagesForDesktop = () => {
    return (
      <>
        <div className='images-container' style={imageStyles}>
          <div className='photo-container'>
            <Photo
              src={image}
              style={{
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}
            />
          </div>
          <Button
            className='tertiary photos'
            text='See all photos'
            onClick={toggle}
          />
          <Modal
            isShowing={isShowing}
            hide={toggle}
            isCloseOnClickOutside={true}
          >
            <PhotoCarousel
              images={imagesMapped}
              showButtons={true}
              isDesktop={isDesktop}
            />
          </Modal>
        </div>
        <style jsx>{`
          div.images-container {
            position: relative;
            display: flex;
            width: 100%;
          }
        `}</style>
        <style global jsx>{`
          div.images-container a.photos {
            width: 9.875rem;
            position: absolute;
            bottom: 2rem;
            right: 2rem;
          }
        `}</style>
      </>
    );
  };

  const getSecondaryImagesForMobile = () => {
    return (
      <>
        <div className='images-container' style={imageStyles}>
          <MobilePhotoContainer
            images={imagesMapped}
            isDesktop={isDesktop}
          />
        </div>
      </>
    );
  };

  const getSecondaryImages = () => {
    return (
      <>
        {isDesktop
          ? getSecondaryImagesForDesktop()
          : getSecondaryImagesForMobile()}
      </>
    );
  };

  return (
    <>
      <div className='container' style={containerStyle}>
        <div
          className='info-container'
          style={{
            ...getCustomInfoContainerStyles(),
            ...infoContainerStyle,
          }}>
          {children ?? getDefaultInfoCard(infoButtonStyle)}
        </div>
        {(!removeImage ? (
          <>
            {(!imagesMapped?.length && image?.url) ? (
              <div className='image-container' style={imageStyles} >
                <a  href={imageUrl ? imageUrl.url : null}></a>
              </div>
            ) : (
              getSecondaryImages()
            )}
          </>
        ): (''))}
        
      </div>

      <style jsx>{`
        div.container {
          display: flex;
          flex-direction: column-reverse;
          gap: 1rem;
        }

        div.container div.info-container {
          text-align: center;
        }

        div.container .image-container {
          background-image: url(${image?.url || ''});
          background-position: center top;
          background-repeat: no-repeat;
          background-size: cover;
          height: 15.625rem;
        }

        div.container .image-container a {
          width: 100%;
        }

        @media only screen and (min-width: ${theme.breakpoints.tabletMin}) {
          div.container .image-container {
            height: 34.625rem;
          }
        }

        @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
          div.container {
            flex-direction: row;
          }

          div.container div.info-container {
            display: flex;
            width: 100%;
            text-align: left;
            padding-right: 2.0625rem;
          }

          div.container .image-container {
            display: flex;
            width: 100%;
            height: 30rem;
            max-height: 30rem;
          }
        }
      `}</style>
    </>
  );
};

export default StyledCard;
